import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../lib/cart";
import { useSettings } from "../lib/settings";
import { fileUrl, fmtIQD } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import RequireAuth from "../components/RequireAuth";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export default function Cart() {
  return (
    <RequireAuth>
      <CartInner />
    </RequireAuth>
  );
}

function CartInner() {
  const { items, remove, setQty, total } = useCart();
  const { settings } = useSettings();
  const nav = useNavigate();
  const freeDeliveryEnabled = settings?.free_delivery_enabled === true;
  const threshold = freeDeliveryEnabled ? Number(settings?.free_delivery_threshold || 0) : 0;
  const remaining = threshold > 0 ? Math.max(0, threshold - total) : 0;
  const progress = threshold > 0 ? Math.min(100, (total / threshold) * 100) : 0;

  return (
    <div className="App">
      <Header />
      <div className="pg-page-top pb-16 min-h-[60vh]">
        <div className="pg-container">
          <h1 className="text-3xl md:text-4xl font-black mb-8">سلة التسوق</h1>

          {items.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingBag size={60} className="mx-auto text-[#3A3D43] mb-4" />
              <p className="text-lg text-gray-400 mb-6">سلتك فارغة حالياً</p>
              <Link to="/" className="bg-[#EF1B17] hover:bg-[#FF3B30] text-white font-bold px-6 py-3 rounded-md pg-lift inline-block">
                ابدأ التسوق
              </Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-3">
                {items.map((it) => (
                  <div key={it.key} data-testid={`cart-item-${it.product_id}`} className="flex gap-4 bg-[#24262B] border border-[#3A3D43] rounded-lg p-3">
                    <div className="w-24 h-24 rounded bg-[#191A1D] p-2 shrink-0">
                      <img src={fileUrl(it.cover_image)} alt={it.name} className="object-contain w-full h-full" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold line-clamp-1">{it.name}</div>
                      {it.variant_name && <div className="text-xs text-gray-400 mt-1">{it.variant_name}</div>}
                      <div className="text-[#EF1B17] font-black mt-1">{fmtIQD(it.unit_price)}</div>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button data-testid={`cart-remove-${it.product_id}`} onClick={() => remove(it.key)} className="text-gray-500 hover:text-[#EF1B17]"><Trash2 size={16} /></button>
                      <div className="flex items-center border border-[#3A3D43] rounded">
                        <button onClick={() => setQty(it.key, it.quantity - 1)} className="px-2 py-1"><Minus size={12} /></button>
                        <span className="w-8 text-center text-sm font-bold">{it.quantity}</span>
                        <button onClick={() => setQty(it.key, it.quantity + 1)} className="px-2 py-1"><Plus size={12} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#24262B] border border-[#3A3D43] rounded-lg p-6 h-fit sticky top-32">
                <h3 className="font-black text-xl mb-4">ملخص الطلب</h3>
                {threshold > 0 && (
                  <div data-testid="cart-free-hint" className="mb-4 p-3 rounded-md border border-[#3A3D43] bg-[#191A1D]">
                    {remaining > 0 ? (
                      <>
                        <div className="text-xs text-gray-300 mb-2">
                          أضف <span className="font-bold text-[#EF1B17]">{fmtIQD(remaining)}</span> للحصول على توصيل مجاني
                        </div>
                        <div className="h-1.5 rounded-full bg-[#3A3D43] overflow-hidden">
                          <div className="h-full bg-gradient-to-l from-[#EF1B17] to-[#FF3B30] transition-all duration-500" style={{ width: `${progress}%` }} />
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-green-400 font-bold">🚚 مبروك! توصيلك مجاني</div>
                    )}
                  </div>
                )}
                <div className="space-y-2 text-sm border-b border-[#3A3D43] pb-3 mb-3">
                  <div className="flex justify-between"><span className="text-gray-400">المجموع الفرعي</span><span>{fmtIQD(total)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">التوصيل</span><span className="text-green-400">يُحدد عند الطلب</span></div>
                </div>
                <div className="flex justify-between text-lg font-black mb-6">
                  <span>الإجمالي</span><span>{fmtIQD(total)}</span>
                </div>
                <button data-testid="checkout-btn" onClick={() => nav("/checkout")}
                  className="w-full bg-[#EF1B17] hover:bg-[#FF3B30] text-white font-bold py-3 rounded-md pg-lift">
                  متابعة إلى الدفع
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
